/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/lib/http.ts
 * ----------------------------------------------------------------------------
 * Ponte ÚNICA p/ HTTP no app:
 *  - Resolve base do backend (proxy /api em PROD; variável em DEV).
 *  - Normaliza paths para evitar "/api/api" e duplicação de barras.
 *  - Exports: http (axios), api* helpers, getBaseUrl, normalizePath, etc.
 * NOTA: Não importe ESTE arquivo dentro de '@/lib/hooks/http.ts' para
 *       evitar ciclos. Se existir hooks/http.ts, ele não deve depender daqui.
 * ========================================================================== */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/store/authStore'

/* ------------------------------ Base Resolver ------------------------------ */

function trimSlashEnd(s: string) {
  return s.replace(/\/+$/g, '')
}
function trimSlashStart(s: string) {
  return s.replace(/^\/+/g, '')
}
function isAbsUrl(u: string) {
  return /^https?:\/\//i.test(u)
}

/** Retorna a base do backend.
 *  - PROD: usa proxy padrão "/api" (nginx/caddy).
 *  - DEV: usa VITE_API_BASE_FORCE > VITE_API_BASE_URL > VITE_API_BASE > "/api".
 */
export function getBaseUrl(): string {
  const env = import.meta.env as any
  const force: string | undefined = env?.VITE_API_BASE_FORCE
  if (force) return trimSlashEnd(force)

  // Em dev/local geralmente apontamos direto pro backend
  if (!env?.PROD) {
    const dev = env?.VITE_API_BASE_URL || env?.VITE_API_BASE || '/api'
    return trimSlashEnd(dev)
  }

  // Produção: sempre via reverse proxy /api
  return '/api'
}

/** Junta base + path evitando '/api/api' e barras duplicadas. */
export function normalizePath(path: string): string {
  if (!path) return getBaseUrl()
  if (isAbsUrl(path)) return path

  const base = getBaseUrl()
  const cleanBase = trimSlashEnd(base)
  let p = path

  // Se o base já termina com /api e o path começa com /api, remove a primeira ocorrência
  if (/\/api$/i.test(cleanBase) && /^\/?api(\/|$)/i.test(p)) {
    p = p.replace(/^\/?api/i, '') // tira o prefixo 'api'
  }

  // Garante única barra entre base e path
  const right = '/' + trimSlashStart(p)
  return cleanBase + right
}

/* ------------------------------ Axios client ------------------------------- */

const http: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  // withCredentials: true, // habilite se precisar enviar cookies
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
})

// Le o access token da MESMA fonte que a instancia irma (src/lib/api.ts:getAccess):
// primeiro o store (accessToken), depois localStorage (movatic.access e legados).
function readAccessToken(): string {
  try {
    const s = (useAuthStore as any)?.getState?.()
    const fromStore =
      s?.accessToken ?? s?.access ?? s?.token ?? s?.state?.accessToken
    if (fromStore) return fromStore as string
  } catch {
    /* ignore */
  }
  for (const k of [
    'movatic.access',
    'accessToken',
    'access',
    'auth.access',
    'token',
  ]) {
    try {
      const v = localStorage.getItem(k)
      if (v) return v
    } catch {
      /* ignore */
    }
  }
  return ''
}

// Interceptor unico: (1) normaliza o path relativo; (2) injeta o Bearer.
// Sem (2), o fetchMe (que usa ESTA instancia) ia sem token -> 401 -> user vazio.
http.interceptors.request.use((cfg) => {
  // Se o caller passou um URL relativo, normalizamos nós mesmos.
  if (cfg.url && !isAbsUrl(cfg.url)) {
    cfg.url = normalizePath(cfg.url)
    // Como definimos url absoluta, anulamos baseURL para evitar join duplo do axios
    cfg.baseURL = ''
  }
  // Auth: so injeta se o caller NAO tiver ja' posto um Authorization (respeita o manual).
  const token = readAccessToken()
  if (token) {
    const h: any = (cfg.headers ||= {} as any)
    const hasAuth =
      typeof h.has === 'function'
        ? h.has('Authorization')
        : !!h['Authorization']
    if (!hasAuth) {
      if (typeof h.set === 'function') h.set('Authorization', `Bearer ${token}`)
      else h['Authorization'] = `Bearer ${token}`
    }
  }
  return cfg
})

/* ------------------------------ API helpers -------------------------------- */

export async function apiGet<T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return http.get<T>(url, config)
}

export async function apiPost<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return http.post<T>(url, data, config)
}

export async function apiPut<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return http.put<T>(url, data, config)
}

export async function apiPatch<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return http.patch<T>(url, data, config)
}

export async function apiDelete<T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return http.delete<T>(url, config)
}

/** Ponto único para lidar com diferenças de endpoint de stock, se houver. */
export function resolveStockEndpoint(): string {
  const env = import.meta.env as any
  const fromEnv = env?.VITE_STOCK_ENDPOINT
  // Valor padrão histórico do nosso backend:
  const fallback = 'api/inventory/stock-items'
  const resolved = trimSlashStart(fromEnv || fallback)
  // Devolvemos SEM barra inicial; quem consumir deve passar por normalizePath
  return resolved
}

/* ------------------------------ Exports ------------------------------------ */

export { http } // nomeado
export default http // default

// Para compat com o código existente que importava do antigo shim:
export { getBaseUrl as getApiBaseUrl } from './http'
